import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { BellRing, BellOff, Clock, Globe2, Info, Laptop, MonitorSmartphone, Moon, Play, Power, Radio, Sparkles, Volume2, XSquare } from 'lucide-react';
import type { NotificationKind, NotificationPrefs, RingSettings, RingtoneId } from '@mailpilot/shared';
import { useNotifications } from '../../lib/notifications';
import { MAX_RING_DURATION_SECONDS, MIN_RING_INTERVAL_MINUTES } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relativeTime, fullDate } from '../../lib/format';
import { bridge } from '../../lib/desktop';
import { RINGTONES } from '../../lib/sound';
import { useRing } from '../../lib/ring';
import { Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Field, Input, Notice, Segmented, Select, Skeleton, useToast } from '../../components/ui';
import { Rows, SettingRow, StatusPill, SwitchRow } from '../../components/settings/SettingsPrimitives';
import { useMe, useUpdateSettings } from '../../hooks/useMe';
import { SettingsTitle } from './SettingsLayout';
import { DailySummaryCard } from '../../components/settings/DailySummaryCard';

/** Numeric input that keeps a local draft and commits a clamped value on blur / Enter. */
function NumberSetting({
  label,
  hint,
  value,
  min,
  max,
  suffix,
  onCommit,
  disabled,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  onCommit: (v: number) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const n = Math.round(Number(draft));
  const invalid = draft.trim() === '' || !Number.isFinite(n) || n < min || n > max;
  const commit = () => {
    if (!Number.isFinite(n) || draft.trim() === '') {
      setDraft(String(value));
      return;
    }
    const c = Math.max(min, Math.min(max, n));
    setDraft(String(c));
    if (c !== value) onCommit(c);
  };
  return (
    <Field label={label} hint={hint} error={invalid ? `Enter ${min}–${max} ${suffix}.` : undefined}>
      {(id) => (
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
            }}
            className="pr-20"
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[0.8125rem] text-muted">{suffix}</span>
        </div>
      )}
    </Field>
  );
}

type IntervalChoice = '60' | '120' | 'custom';

function RingingCard() {
  const me = useMe();
  const update = useUpdateSettings();
  const ring = useRing();
  const toast = useToast();
  const s = me.data?.settings;
  const [customMode, setCustomMode] = useState(false);
  const [volume, setVolume] = useState<number>(s?.ring.volume ?? 0.8);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    if (s) setVolume(s.ring.volume);
  }, [s?.ring.volume]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return null;
  const r = s.ring;
  const patchRing = (p: Partial<RingSettings>) => update.mutate({ ring: { ...r, ...p } });
  const choice: IntervalChoice = customMode || (r.intervalMinutes !== 60 && r.intervalMinutes !== 120) ? 'custom' : (String(r.intervalMinutes) as IntervalChoice);
  const commitVolume = (v: number) => {
    if (Math.abs(v - r.volume) > 0.001) patchRing({ volume: v });
  };
  const test = async () => {
    setTesting(true);
    try {
      await ring.testSound(r.ringtone, volume, Math.min(r.ringDurationSeconds, 8));
    } catch (e) {
      toast.error(e);
    } finally {
      setTimeout(() => setTesting(false), Math.min(r.ringDurationSeconds, 8) * 1000);
    }
  };

  return (
    <Card>
      <CardHeader
        icon={<BellRing className="h-5 w-5" />}
        title="Persistent ringing"
        description="After every automatic response, MailPilot rings until you explicitly mark the item “Seen / Acknowledged”. Opening the email or dismissing a notification does not stop it."
      />
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        <Rows>
          <SwitchRow id="ring-enabled" size="lg" checked={r.enabled} onChange={(v) => patchRing({ enabled: v })} label="Ring for automatic responses" description="Rings immediately, then repeats on the interval below until acknowledged. Acknowledging on any device stops it everywhere." />
          <fieldset disabled={!r.enabled} className="py-4 last:pb-0 disabled:opacity-60">
            <legend className="sr-only">Ringing details</legend>
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink" id="interval-label">
                    Repeat every
                  </p>
                  <p className="mt-0.5 text-[0.8125rem] text-muted">How often it rings again while something is still unacknowledged.</p>
                </div>
                <Segmented<IntervalChoice>
                  label="Repeat interval"
                  value={choice}
                  onChange={(v) => {
                    if (v === 'custom') {
                      setCustomMode(true);
                    } else {
                      setCustomMode(false);
                      patchRing({ intervalMinutes: Number(v) });
                    }
                  }}
                  options={[
                    { value: '60', label: '60 min' },
                    { value: '120', label: '120 min' },
                    { value: 'custom', label: 'Custom' },
                  ]}
                />
              </div>
              {choice === 'custom' && (
                <div className="max-w-xs">
                  <NumberSetting label="Custom interval" hint={`Minimum ${MIN_RING_INTERVAL_MINUTES} minutes.`} value={r.intervalMinutes} min={MIN_RING_INTERVAL_MINUTES} max={24 * 60} suffix="minutes" onCommit={(v) => patchRing({ intervalMinutes: v })} />
                </div>
              )}

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Ringtone">
                  {(id) => (
                    <Select id={id} value={r.ringtone} onChange={(e) => patchRing({ ringtone: e.target.value as RingtoneId })}>
                      {RINGTONES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="ring-volume" className="text-[0.8125rem] font-medium text-ink">
                      Volume
                    </label>
                    <span className="rounded-lg bg-sunken px-2 py-0.5 text-xs font-semibold tabular-nums text-ink">{Math.round(volume * 100)}%</span>
                  </div>
                  <div className="flex h-10 items-center gap-3">
                    <Volume2 className="h-4 w-4 shrink-0 text-muted" aria-hidden />
                    <input
                      id="ring-volume"
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={volume}
                      aria-valuetext={`${Math.round(volume * 100)} percent`}
                      onChange={(e) => setVolume(Number(e.target.value))}
                      onPointerUp={(e) => commitVolume(Number(e.currentTarget.value))}
                      onKeyUp={(e) => commitVolume(Number(e.currentTarget.value))}
                      onBlur={(e) => commitVolume(Number(e.currentTarget.value))}
                      className="h-2 flex-1 cursor-pointer accent-[rgb(var(--primary))]"
                    />
                  </div>
                </div>
                <NumberSetting label="Ring duration" hint="Each sound session stops after this long, then repeats at the next interval if still pending." value={r.ringDurationSeconds} min={5} max={MAX_RING_DURATION_SECONDS} suffix="seconds" onCommit={(v) => patchRing({ ringDurationSeconds: v })} />
                <NumberSetting label="Snooze length" hint="Used by the Snooze button on ring alerts." value={r.snoozeMinutes} min={1} max={24 * 60} suffix="minutes" onCommit={(v) => patchRing({ snoozeMinutes: v })} />
              </div>
            </div>
          </fieldset>
          <SettingRow
            label="Test the sound"
            description="Plays the selected ringtone at the chosen volume for up to 8 seconds."
            control={
              <Button size="sm" icon={<Play className="h-4 w-4" aria-hidden />} loading={testing} onClick={() => void test()}>
                Test sound
              </Button>
            }
          />
        </Rows>
      </CardBody>
    </Card>
  );
}

function QuietHoursCard() {
  const me = useMe();
  const update = useUpdateSettings();
  const s = me.data?.settings;
  const [start, setStart] = useState(s?.quietHours.start ?? '22:00');
  const [end, setEnd] = useState(s?.quietHours.end ?? '07:00');
  const [tz, setTz] = useState(s?.timezone ?? '');
  useEffect(() => {
    if (!s) return;
    setStart(s.quietHours.start);
    setEnd(s.quietHours.end);
    setTz(s.timezone);
  }, [s?.quietHours.start, s?.quietHours.end, s?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return null;
  const q = s.quietHours;
  const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTz = (v: string) => {
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: v });
      return true;
    } catch {
      return false;
    }
  };
  const tzInvalid = tz.trim() !== '' && !validTz(tz.trim());
  const commitTz = () => {
    const v = tz.trim();
    if (!v || !validTz(v)) {
      setTz(s.timezone);
      return;
    }
    if (v !== s.timezone) update.mutate({ timezone: v });
  };

  return (
    <Card>
      <CardHeader icon={<Moon className="h-5 w-5" />} title="Quiet hours" description="No sound during these hours. Reminders that fall due are deferred to the end of the quiet window — they are not dropped." />
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        <Rows>
          <SwitchRow id="quiet-hours" checked={q.enabled} onChange={(v) => update.mutate({ quietHours: { ...q, enabled: v } })} label="Enable quiet hours" description={q.enabled ? `Silent from ${q.start} to ${q.end}.` : 'Sounds can play at any time.'} />
          <div className="space-y-5 py-4 last:pb-0">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Start">
                {(id) => (
                  <Input
                    id={id}
                    type="time"
                    value={start}
                    disabled={!q.enabled}
                    onChange={(e) => setStart(e.target.value)}
                    onBlur={() => {
                      if (start && start !== q.start) update.mutate({ quietHours: { ...q, start } });
                    }}
                  />
                )}
              </Field>
              <Field label="End">
                {(id) => (
                  <Input
                    id={id}
                    type="time"
                    value={end}
                    disabled={!q.enabled}
                    onChange={(e) => setEnd(e.target.value)}
                    onBlur={() => {
                      if (end && end !== q.end) update.mutate({ quietHours: { ...q, end } });
                    }}
                  />
                )}
              </Field>
            </div>
            <Field label="Time zone" hint={deviceTz !== s.timezone ? `IANA name, e.g. Europe/London. This device is in ${deviceTz}.` : 'IANA name, e.g. Europe/London. Used for quiet hours, business hours and reminder schedules.'} error={tzInvalid ? 'Unknown time zone.' : undefined}>
              {(id) => (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id={id}
                    value={tz}
                    spellCheck={false}
                    autoComplete="off"
                    onChange={(e) => setTz(e.target.value)}
                    onBlur={commitTz}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitTz();
                    }}
                  />
                  <Button
                    className="shrink-0"
                    icon={<Globe2 className="h-4 w-4" aria-hidden />}
                    disabled={deviceTz === s.timezone}
                    onClick={() => {
                      setTz(deviceTz);
                      update.mutate({ timezone: deviceTz });
                    }}
                  >
                    Use this device's time zone
                  </Button>
                </div>
              )}
            </Field>
          </div>
        </Rows>
      </CardBody>
    </Card>
  );
}

function ThisDeviceCard() {
  const b = bridge();
  const ring = useRing();
  const toast = useToast();
  const [launch, setLaunch] = useState<boolean | null>(null);
  useEffect(() => {
    if (!b) return;
    b.getLaunchAtLogin().then(setLaunch).catch(() => setLaunch(false));
  }, [b]);

  if (b) {
    return (
      <Card>
        <CardHeader icon={<Laptop className="h-5 w-5" />} title="Desktop app" description="The background process rings even when the window is closed." />
        <CardDivider className="mt-5" />
        <CardBody className="!py-5">
          {launch === null ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Rows>
              <SwitchRow
                id="launch-at-login"
                checked={launch}
                onChange={async (v) => {
                  setLaunch(v);
                  try {
                    await b.setLaunchAtLogin(v);
                    toast.success(v ? 'MailPilot will start at login' : 'MailPilot will not start at login');
                  } catch (e) {
                    setLaunch(!v);
                    toast.error(e);
                  }
                }}
                label="Launch at login"
                description="Start MailPilot in the background when you sign in to your computer so reminders keep ringing after a restart."
              />
            </Rows>
          )}
        </CardBody>
      </Card>
    );
  }

  const perm = ring.notificationPermission;
  const permLabel = perm === 'granted' ? 'Allowed' : perm === 'denied' ? 'Blocked' : perm === 'unsupported' ? 'Not supported' : 'Not asked yet';
  const permTone = perm === 'granted' ? 'success' : perm === 'denied' ? 'danger' : 'neutral';
  return (
    <Card>
      <CardHeader icon={<MonitorSmartphone className="h-5 w-5" />} title="This browser tab" description="Web ringing only works while this tab is open. Install the desktop app for background ringing." />
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        <Rows>
          <SettingRow
            icon={<Volume2 className="h-4 w-4" aria-hidden />}
            label={
              <span className="inline-flex flex-wrap items-center gap-2">
                Sound in this tab <StatusPill tone={ring.soundEnabled ? 'success' : 'neutral'}>{ring.soundEnabled ? 'Enabled' : 'Not enabled'}</StatusPill>
              </span>
            }
            description={ring.soundEnabled ? 'This tab can play ring alerts.' : 'Browsers only allow sound after you interact with the page. Enable it once per tab.'}
            control={
              <Button
                size="sm"
                variant={ring.soundEnabled ? 'secondary' : 'primary'}
                icon={<Volume2 className="h-4 w-4" aria-hidden />}
                onClick={async () => {
                  const ok = await ring.enableSound();
                  if (ok) toast.success('Sound enabled in this tab');
                  else toast.error(new Error('This browser blocked audio playback.'));
                }}
              >
                {ring.soundEnabled ? 'Sound enabled' : 'Enable sound'}
              </Button>
            }
          />
          <SettingRow
            icon={<BellRing className="h-4 w-4" aria-hidden />}
            label={
              <span className="inline-flex flex-wrap items-center gap-2">
                Browser notifications <StatusPill tone={permTone}>{permLabel}</StatusPill>
              </span>
            }
            description={perm === 'denied' ? 'Notifications are blocked. Allow them in your browser’s site settings.' : 'Shows a system notification alongside the sound when this tab is in the background.'}
            control={
              <Button size="sm" variant={perm === 'granted' ? 'secondary' : 'primary'} icon={<BellRing className="h-4 w-4" aria-hidden />} disabled={perm === 'granted' || perm === 'denied' || perm === 'unsupported'} onClick={() => void ring.requestNotificationPermission()}>
                {perm === 'granted' ? 'Allowed' : 'Allow notifications'}
              </Button>
            }
          />
        </Rows>
      </CardBody>
    </Card>
  );
}

function AvailabilityCard() {
  const ring = useRing();
  const status = useQuery({ queryKey: qk.ringStatus, queryFn: () => api('GET /api/ring/status'), refetchInterval: 30_000 });
  const devices = status.data?.devices ?? [];
  const reason = ring.unavailableReason;
  return (
    <Card>
      <CardHeader
        icon={<Radio className="h-5 w-5" />}
        title="Ring availability"
        description="Only one device rings at a time (it holds the ringing lease) so you don't hear duplicates."
        actions={
          reason ? (
            <StatusPill tone="warn">This device can't ring</StatusPill>
          ) : (
            <StatusPill tone="success">This device can ring{ring.mode === 'web' ? ' while open' : ''}</StatusPill>
          )
        }
      />
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        {reason && (
          <Notice tone="warn" icon={<BellOff className="h-4 w-4" aria-hidden />} className="mb-5">
            {reason}
          </Notice>
        )}
        {status.isLoading ? (
          <div className="space-y-4" aria-hidden>
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : status.error ? (
          <ErrorState compact error={status.error} onRetry={() => void status.refetch()} />
        ) : devices.length === 0 ? (
          <EmptyState icon={<Radio className="h-6 w-6" aria-hidden />} title="No devices reporting" description="Devices appear here once the desktop app or an open MailPilot tab checks in." className="!py-6" />
        ) : (
          <ul className="divide-y divide-line/70">
            {devices.map((d) => (
              <li key={d.sessionId} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3.5 first:pt-0 last:pb-0">
                <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', d.holdsLease ? 'bg-primary-soft text-primary-ink' : 'bg-sunken text-muted')}>
                  <MonitorSmartphone className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium text-ink">{d.deviceName}</span>
                    {d.holdsLease && <Badge tone="primary">Ringing lease</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-muted" title={fullDate(d.lastHeartbeatAt)}>
                    Last check-in {relativeTime(d.lastHeartbeatAt)}
                    {!d.canRing && d.unavailableReason ? ` · ${d.unavailableReason}` : ''}
                  </p>
                </div>
                <StatusPill tone={d.canRing ? 'success' : 'warn'}>{d.canRing ? 'Can ring' : 'Unavailable'}</StatusPill>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ExplanationCard() {
  const rows = [
    {
      icon: <XSquare className="h-4 w-4" aria-hidden />,
      title: 'Closing the window',
      text: 'The desktop app keeps running in the tray / menu bar and keeps ringing. Closing a browser tab stops web ringing in that tab.',
    },
    {
      icon: <Power className="h-4 w-4" aria-hidden />,
      title: 'Quitting the background app',
      text: 'No ringing on this device. The server keeps the schedule and other signed-in devices can ring; reminders resume here when you reopen the app.',
    },
    {
      icon: <Clock className="h-4 w-4" aria-hidden />,
      title: 'Powering off or sleeping the device',
      text: 'Nothing can ring on it. The backend keeps sync, automations and reminder schedules running, and recovers any reminders that fell due when the device restarts.',
    },
  ];
  return (
    <Card>
      <CardHeader icon={<Info className="h-5 w-5" />} title="When ringing works" description="What happens to reminders in common situations." />
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        <Rows>
          {rows.map((r) => (
            <SettingRow key={r.title} icon={r.icon} label={r.title} description={r.text} />
          ))}
        </Rows>
      </CardBody>
    </Card>
  );
}

const PREVIEWS: { kind: NotificationKind; label: string }[] = [
  { kind: 'mail', label: 'New email' },
  { kind: 'ai_draft', label: 'AI draft' },
  { kind: 'ai_analysis', label: 'AI insight' },
  { kind: 'auto_reply', label: 'Automation' },
  { kind: 'send_failed', label: 'Problem' },
];

function IslandPreview({ muted }: { muted: boolean }) {
  return (
    <div className={clsx('relative flex justify-center overflow-hidden rounded-2xl px-4 pb-7 pt-5 transition-opacity', muted && 'opacity-50')} aria-hidden style={{ background: 'linear-gradient(180deg, rgb(var(--sunken) / 0.9), rgb(var(--sunken) / 0.15))' }}>
      <span className="absolute left-1/2 top-0 h-px w-40 -translate-x-1/2 bg-gradient-to-r from-transparent via-line to-transparent" />
      <div className="flex w-full max-w-[360px] items-center gap-3 rounded-[22px] bg-[#0A0A0C] py-2.5 pl-3 pr-3.5 text-white shadow-[0_18px_44px_-14px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.08)]">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-primary text-xs font-semibold text-primary-fg">DL</span>
        <span className="min-w-0 flex-1">
          <span className="block text-[0.5625rem] font-semibold uppercase tracking-[0.1em] text-white/50">Work · New email</span>
          <span className="block truncate text-[0.8125rem] font-semibold leading-snug">Dana Lee</span>
          <span className="block truncate text-[0.6875rem] text-white/65">Re: Quarterly report</span>
        </span>
        <svg viewBox="0 0 20 20" className="h-5 w-5 shrink-0 -rotate-90" aria-hidden>
          <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
          <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeDasharray="50.3" strokeDashoffset="18" />
        </svg>
      </div>
    </div>
  );
}

function IslandCard() {
  const me = useMe();
  const update = useUpdateSettings();
  const toast = useToast();
  const { preview } = useNotifications();
  const n = me.data?.settings.notifications;
  if (!n) return null;
  const patch = (p: Partial<NotificationPrefs>) => update.mutate({ notifications: { ...n, ...p } });
  const desktop = !!bridge();
  const run = async (kind: NotificationKind) => {
    try {
      await preview(kind);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Card>
      <CardHeader
        icon={<Sparkles className="h-5 w-5" />}
        title="Notification island"
        description="New email, AI and automation updates slide down from the top of your screen as a compact island. Hover it to pause and see actions. Choose where it appears and how it looks below. These settings are personal."
      />
      <CardBody className="space-y-5">
        <IslandPreview muted={!n.island} />
        <Rows>
          <SwitchRow id="island-enabled" size="lg" checked={n.island} onChange={(v) => patch({ island: v })} label="Show the notification island" description="Turn off to keep MailPilot quiet; notifications still collect in the bell at the top." />
        </Rows>
        <fieldset disabled={!n.island} className="disabled:opacity-60">
          <legend className="sr-only">Island details</legend>
          <div className="grid gap-x-10 sm:grid-cols-2">
            <Rows>
              <SwitchRow id="n-newmail" checked={n.newMail} onChange={(v) => patch({ newMail: v })} label="New email" description="Grouped per account when several arrive together." />
              <SwitchRow id="n-ai" checked={n.ai} onChange={(v) => patch({ ai: v })} label="AI drafts & insights" description="When a draft is ready or someone is waiting on you." />
              <SwitchRow id="n-sound" checked={n.sound} onChange={(v) => patch({ sound: v })} label="Soft chime" description="A short, quiet sound when the island appears." />
            </Rows>
            <Rows>
              <SwitchRow id="n-automation" checked={n.automation} onChange={(v) => patch({ automation: v })} label="Automation & reminders" description="Automatic replies sent or held, files requested." />
              <SwitchRow id="n-system" checked={n.system} onChange={(v) => patch({ system: v })} label="Problems & team" description="Failed sends, accounts to reconnect, people joining." />
              <SwitchRow
                id="n-screen"
                checked={n.desktopScreenIsland}
                onChange={(v) => patch({ desktopScreenIsland: v })}
                label="Show above other apps"
                description={desktop ? 'The desktop app shows the island at the top of your screen even when MailPilot is in the background.' : 'Desktop app only. In the browser the island appears at the top of this tab.'}
              />
            </Rows>
          </div>
          <div className="mt-2 border-t border-line/70 pt-4">
            <Rows>
              <SettingRow
                label="Stay on screen for"
                description="The island slides away by itself after this long."
                control={<Segmented<string> label="Island duration" value={String(n.durationSeconds)} onChange={(v) => patch({ durationSeconds: Number(v) })} options={[4, 6, 10, 15].map((sec) => ({ value: String(sec), label: `${sec} s` }))} />}
              />
              <SettingRow
                label="Position"
                description="Where the island drops in."
                control={<Segmented<string> label="Island position" value={n.islandPosition} onChange={(v) => patch({ islandPosition: v as typeof n.islandPosition })} options={[{ value: 'center', label: 'Top centre' }, { value: 'right', label: 'Top right' }]} />}
              />
              <SettingRow
                label="Gap from the top"
                description="Space between the island and the top of the screen."
                control={<Segmented<string> label="Gap from the top" value={n.islandGap} onChange={(v) => patch({ islandGap: v as typeof n.islandGap })} options={[{ value: 'snug', label: 'Snug' }, { value: 'comfortable', label: 'Comfortable' }, { value: 'roomy', label: 'Roomy' }]} />}
              />
              <SettingRow
                label="Style"
                description="Black like the hardware island, a softer graphite, or a deep tint of your accent colour."
                control={<Segmented<string> label="Island style" value={n.islandStyle} onChange={(v) => patch({ islandStyle: v as typeof n.islandStyle })} options={[{ value: 'black', label: 'Black' }, { value: 'graphite', label: 'Graphite' }, { value: 'accent', label: 'Accent' }]} />}
              />
              <SettingRow
                label="Animation"
                description="Smooth glides in and settles; Lively bounces; Subtle barely moves."
                control={<Segmented<string> label="Island animation" value={n.islandMotion} onChange={(v) => patch({ islandMotion: v as typeof n.islandMotion })} options={[{ value: 'smooth', label: 'Smooth' }, { value: 'lively', label: 'Lively' }, { value: 'subtle', label: 'Subtle' }]} />}
              />
              <SettingRow
                label="Try it"
                description="Previews travel the same live path as real notifications, so they also confirm this device is connected."
                control={
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {PREVIEWS.map((p) => (
                      <Button key={p.kind} size="sm" variant="outline" icon={<Play className="h-3.5 w-3.5" aria-hidden />} onClick={() => void run(p.kind)}>
                        {p.label}
                      </Button>
                    ))}
                  </div>
                }
              />
            </Rows>
          </div>
        </fieldset>
      </CardBody>
    </Card>
  );
}

export function NotificationSettings() {
  const me = useMe();
  const title = <SettingsTitle title="Notifications & ringing" description="Choose how MailPilot tells you about new email and AI activity, how it rings after automatic responses, and which devices can ring." />;
  if (me.isLoading) {
    return (
      <div>
        {title}
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-96 w-full rounded-panel" />
          <Skeleton className="h-64 w-full rounded-panel" />
        </div>
      </div>
    );
  }
  if (me.error || !me.data) {
    return (
      <div>
        {title}
        <Card>
          <ErrorState error={me.error} onRetry={() => void me.refetch()} />
        </Card>
      </div>
    );
  }
  return (
    <div>
      {title}
      <div className="space-y-6">
        <IslandCard />
        <DailySummaryCard />
        {me.data.workspace.kind === 'organization' && me.data.workspace.role === 'member' && (
          <Notice tone="info" icon={<Info className="h-4 w-4" aria-hidden />}>
            Ringing and quiet hours are shared by everyone in {me.data.workspace.name} and are set by its admins. The island settings above are just for you.
          </Notice>
        )}
        <RingingCard />
        <ThisDeviceCard />
        <QuietHoursCard />
        <AvailabilityCard />
        <ExplanationCard />
      </div>
    </div>
  );
}
